(() => {
  const GLOBAL_KEY = '__landingProductPurchaseCountdown';
  const TIMER_SELECTOR = '[data-landing-sale-countdown]';
  const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll(document);
    return;
  }

  const instances = new WeakMap();

  function storageKey() {
    return `landing-sale-deadline:${window.location.pathname}:global`;
  }

  function readStoredDeadline(key) {
    let deadline = 0;

    try {
      deadline = Number(window.localStorage?.getItem(key));
    } catch (error) {
      deadline = 0;
    }

    return Number.isFinite(deadline) && deadline > 0 ? deadline : 0;
  }

  function saveDeadline(deadline) {
    try {
      window.localStorage?.setItem(storageKey(), String(deadline));
    } catch (error) {
      // Storage can be unavailable in privacy modes; the in-memory deadline still works for this page view.
    }
  }

  function storedDeadline(timer) {
    const persistedDeadline = readStoredDeadline(storageKey());

    if (persistedDeadline) return persistedDeadline;

    // Preserve an existing visitor's cycle while moving away from the former offer-specific key.
    const legacyCountdownKey = timer.dataset.countdownKey;
    if (!legacyCountdownKey) return 0;

    const legacyKey = `landing-sale-deadline:${window.location.pathname}:${legacyCountdownKey}`;
    const legacyDeadline = readStoredDeadline(legacyKey);

    if (legacyDeadline) saveDeadline(legacyDeadline);
    return legacyDeadline;
  }

  function persistentFallbackDeadline(timer) {
    const persistedDeadline = storedDeadline(timer);

    if (persistedDeadline) return persistedDeadline;

    const duration = Math.max(1, Number(timer.dataset.countdownDuration) || 86400);
    const deadline = Date.now() + duration * 1000;
    saveDeadline(deadline);

    return deadline;
  }

  function resolveDeadline(timer) {
    const configuredEnd = Date.parse(timer.dataset.countdownEnd || '');
    const persistedDeadline = storedDeadline(timer);

    if (Number.isFinite(configuredEnd) && configuredEnd > Date.now()) return configuredEnd;
    if (persistedDeadline) return persistedDeadline;
    if (Number.isFinite(configuredEnd)) return configuredEnd;
    return persistentFallbackDeadline(timer);
  }

  function formatRemaining(milliseconds) {
    const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return [hours, minutes, seconds]
      .map((value) => String(value).padStart(2, '0'))
      .join(':');
  }

  function initTimer(timer) {
    if (!timer || instances.has(timer)) return;

    let deadline = resolveDeadline(timer);
    let intervalId = 0;
    let renderedSeconds = null;
    let startNextCycle = false;

    function render(milliseconds) {
      renderedSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
      timer.textContent = formatRemaining(milliseconds);
      timer.setAttribute('datetime', new Date(deadline).toISOString());
    }

    function reset() {
      deadline = Date.now() + DAY_IN_MILLISECONDS;
      saveDeadline(deadline);
      startNextCycle = false;
      render(DAY_IN_MILLISECONDS);
    }

    function update(isInitialUpdate = false) {
      if (startNextCycle) {
        reset();
        return;
      }

      const remaining = deadline - Date.now();

      if (remaining <= 0) {
        if (!isInitialUpdate && renderedSeconds === 1 && remaining > -1000) {
          render(0);
          startNextCycle = true;
          return;
        }

        reset();
        return;
      }

      render(remaining);
    }

    update(true);
    intervalId = window.setInterval(update, 1000);

    instances.set(timer, {
      destroy() {
        if (intervalId) window.clearInterval(intervalId);
        instances.delete(timer);
      }
    });
  }

  function initAll(scope = document) {
    if (scope.matches?.(TIMER_SELECTOR)) initTimer(scope);
    scope.querySelectorAll?.(TIMER_SELECTOR).forEach(initTimer);
  }

  function destroyAll(scope) {
    if (!scope) return;

    const timers = [];
    if (scope.matches?.(TIMER_SELECTOR)) timers.push(scope);
    scope.querySelectorAll?.(TIMER_SELECTOR).forEach((timer) => timers.push(timer));
    timers.forEach((timer) => instances.get(timer)?.destroy());
  }

  window[GLOBAL_KEY] = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();

(() => {
  const GLOBAL_KEY = '__landingProductMediaSync';
  const GALLERY_SELECTOR = '.landing-product-purchase .landing-product-media';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll(document);
    return;
  }

  const instances = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function keepThumbnailVisible(gallery, thumbnailButton) {
    const rail = gallery.querySelector('.landing-product-media__thumbnail-list');
    const thumbnailItem = thumbnailButton?.closest('[data-target]');
    if (!rail || !thumbnailItem) return;

    const railRect = rail.getBoundingClientRect();
    const itemRect = thumbnailItem.getBoundingClientRect();
    let nextTop = rail.scrollTop;
    let nextLeft = rail.scrollLeft;

    if (itemRect.top < railRect.top) {
      nextTop -= railRect.top - itemRect.top;
    } else if (itemRect.bottom > railRect.bottom) {
      nextTop += itemRect.bottom - railRect.bottom;
    }

    if (itemRect.left < railRect.left) {
      nextLeft -= railRect.left - itemRect.left;
    } else if (itemRect.right > railRect.right) {
      nextLeft += itemRect.right - railRect.right;
    }

    if (nextTop === rail.scrollTop && nextLeft === rail.scrollLeft) return;

    rail.scrollTo({
      top: nextTop,
      left: nextLeft,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  function syncCurrentMedia(gallery, mediaId) {
    if (!mediaId) return;

    gallery.dataset.activeMediaId = mediaId;
    gallery.querySelectorAll('.landing-product-media__item[data-media-id]').forEach((mediaItem) => {
      mediaItem.classList.toggle('is-active', mediaItem.dataset.mediaId === mediaId);
    });

    let activeThumbnail = null;
    gallery.querySelectorAll('.landing-product-media__thumbnail').forEach((thumbnail) => {
      const isCurrent = thumbnail.closest('[data-target]')?.dataset.target === mediaId;
      thumbnail.toggleAttribute('aria-current', isCurrent);
      if (isCurrent) activeThumbnail = thumbnail;
    });

    keepThumbnailVisible(gallery, activeThumbnail);
  }

  function initGallery(gallery) {
    if (!gallery || instances.has(gallery)) return;

    const viewer = gallery.querySelector('.landing-product-media__viewer');
    const thumbnailRail = gallery.querySelector('.landing-product-media__thumbnail-list');
    if (!viewer) return;

    const onSlideChanged = (event) => {
      const mediaId = event.detail?.currentElement?.dataset.mediaId;
      syncCurrentMedia(gallery, mediaId);
    };

    viewer.addEventListener('slideChanged', onSlideChanged);

    const thumbnailObserver = thumbnailRail
      ? new MutationObserver((mutations) => {
          const activeMutation = mutations.find(
            (mutation) =>
              mutation.type === 'attributes' &&
              mutation.target.matches('.landing-product-media__thumbnail[aria-current="true"]')
          );

          if (!activeMutation) return;

          const activeThumbnail = activeMutation.target;
          const mediaId = activeThumbnail.closest('[data-target]')?.dataset.target;
          if (mediaId) gallery.dataset.activeMediaId = mediaId;
          keepThumbnailVisible(gallery, activeThumbnail);
        })
      : null;

    thumbnailObserver?.observe(thumbnailRail, {
      attributes: true,
      attributeFilter: ['aria-current'],
      subtree: true,
    });

    const initialMedia =
      gallery.querySelector('.landing-product-media__item.is-active[data-media-id]') ||
      gallery.querySelector('.landing-product-media__item[data-media-id]');

    if (initialMedia) {
      syncCurrentMedia(gallery, initialMedia.dataset.mediaId);
      window.requestAnimationFrame(() => gallery.setActiveMedia?.(initialMedia.dataset.mediaId, false));
    }

    instances.set(gallery, {
      destroy() {
        viewer.removeEventListener('slideChanged', onSlideChanged);
        thumbnailObserver?.disconnect();
        instances.delete(gallery);
      },
    });
  }

  function initAll(scope = document) {
    if (scope.matches?.(GALLERY_SELECTOR)) initGallery(scope);
    scope.querySelectorAll?.(GALLERY_SELECTOR).forEach(initGallery);
  }

  function destroyAll(scope) {
    if (!scope) return;

    const galleries = [];
    if (scope.matches?.(GALLERY_SELECTOR)) galleries.push(scope);
    scope.querySelectorAll?.(GALLERY_SELECTOR).forEach((gallery) => galleries.push(gallery));
    galleries.forEach((gallery) => instances.get(gallery)?.destroy());
  }

  window[GLOBAL_KEY] = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
